import { useTimers } from '../store/timers'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import { SyncDatabase } from '../synchronizations/sync'
import { useUserStore } from '../store/useUserStore'
import { getLastSynced } from '../models/LastSyncModel'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch'
import { isNullish } from '../utils'

/******************
 * Auto sync logic
 ******************/

/**sync interval (in ms). Currently 900,000 ms or 15 minutes*/
// const SYNC_INTERVAL = 900000
const SYNC_INTERVAL = 1800000
/**sync interval (in ms) to wait after a store visit concludes.
 * Currently 120,000 ms or 2 minutes*/
const END_STORE_SYNC_INTERVAL = 120000
/**Takes timeout (in ms) to create a timeout function (uses vue use) */
const createAutoSync = (timeout: number) =>
  useTimeoutFn(async () => {
    //cancel if sync in progress
    const { sync } = SyncDatabase()
    await sync()
  }, timeout)

let autoSync = createAutoSync(SYNC_INTERVAL)

/**
 * Auto syncing.
 * This works by reassigning our sync function to a timeout, with the time to sync
 * depending on the last time a sync was concluded, or if a store timer ended.
 * Autosync watches the last synced value for changes, to add another sync
 * based on that interval. On android, backgrounding the app stops the
 * auto sync, and uses a plugin to start a 15 minute interval sync.
 */
export const useAutoSync = () => {
  /**set up auto syncing here */
  watch(
    getLastSynced,
    async () => {
      if (getLastSynced.value) {
        console.log('setting up next sync in 30...')
        //stop a pending sync
        if (autoSync.isPending.value) {
          autoSync.stop()
        }
        //reassign auto sync to standard interval
        autoSync = createAutoSync(SYNC_INTERVAL)
        autoSync.start()
      }
    },
    { deep: true }
  )

  const { getActiveParentTimer, getAllParentTimers } = storeToRefs(useTimers())
  watch(
    getActiveParentTimer,
    async (newValue, oldValue) => {
      //if a store visit ended, set next sync time to 2 mins from now
      if (
        oldValue &&
        oldValue.type == 'store' &&
        oldValue.end_time &&
        !(newValue && newValue.type == 'store')
      ) {
        console.log('you ended a store timer, syncing in 2...')
        //stop a pending sync
        if (autoSync.isPending.value) {
          autoSync.stop()
        }
        //reassign auto sync to standard interval
        autoSync = createAutoSync(END_STORE_SYNC_INTERVAL)
        autoSync.start()
      }
    },
    { deep: true }
  )

  const loggedIn = storeToRefs(useUserStore()).isLoggedIn
  watch(loggedIn, async () => {
    //If a rep logs out, stop the auto sync process
    if (!loggedIn.value) {
      console.log('rep logged out, stopping auto sync')
      autoSync.stop()
    }
  })

  const addCapBG = async () => {
    if (Capacitor.isNativePlatform()) {
      //finally, add an event lister to listen if the app has been moved to the background,
      //so that we can set up background syncing
      await App.addListener('appStateChange', async ({ isActive }) => {
        //if the app awakens, restart autoSync
        if (isActive) {
          if (!autoSync.isPending.value) {
            //reassign auto sync
            //first check if a timer was recently stopped, so we can keep w/in 2 minutes sync
            const lastTimer = getAllParentTimers.value[getAllParentTimers.value.length - 1]
            const currentTimer = getActiveParentTimer.value
            //check if we recently ended a store visit and we currently are not at a store visit.
            if (
              lastTimer &&
              lastTimer.end_time &&
              !(currentTimer && currentTimer.type == 'store')
            ) {
              const msSinceLastParent = new Date().getTime() - lastTimer.end_time?.getTime()
              if (msSinceLastParent < END_STORE_SYNC_INTERVAL) {
                //create next auto sync based on how much time left there was to sync
                createAutoSync(END_STORE_SYNC_INTERVAL - msSinceLastParent)
              }
            } else {
              //Otherwise we should just create a new sync, taking the chance to shave off time if we can
              const msSinceLastSynced =
                new Date().getTime() -
                (!isNullish(getLastSynced.value) ? getLastSynced.value.getTime() : 0)
              if (msSinceLastSynced < SYNC_INTERVAL) {
                //if we're able to shave a few minutes since our last synced value
                createAutoSync(SYNC_INTERVAL - msSinceLastSynced)
              } else {
                //otherwise assign auto sync with standard interval
                autoSync = createAutoSync(SYNC_INTERVAL)
              }
            }
            autoSync.start()
          }
          return
        }
        //otherwise, stop any pending syncs and start the timer in the background
        if (autoSync.isPending.value) {
          autoSync.stop()
        }

        const backgroundSync = async () => {
          // Sanity check to stop any pending syncs and start the timer in the background
          if (autoSync.isPending.value) {
            autoSync.stop()
          }
          return new Promise(async (resolve) => {
            console.log('setting a bg sync')
            const { sync } = SyncDatabase()
            console.log('bg sync')
            await sync()
              .catch(() => {
                resolve(false)
              })
              .then(() => {
                resolve(true)
              })
          })
        }

        // The app state has been changed to inactive.
        // Start the background task by calling `beforeExit`.
        const status = await BackgroundFetch.configure(
          {
            //minimum for the plugin is 15 minutes
            minimumFetchInterval: 15,
          },
          async (taskId) => {
            console.log('[BackgroundFetch] EVENT:', taskId)
            // Perform your work in an awaited Promise
            const result = await backgroundSync()
            console.log('[BackgroundFetch] work complete:', result)
            // [REQUIRED] Signal to the OS that your work is complete.
            await BackgroundFetch.finish(taskId)
          },
          async (taskId) => {
            // The OS has signalled that your remaining background-time has expired.
            // You must immediately complete your work and signal #finish.
            console.log('[BackgroundFetch] TIMEOUT:', taskId)
            // [REQUIRED] Signal to the OS that your work is complete.
            await BackgroundFetch.finish(taskId)
          }
        )

        // Checking BackgroundFetch status:
        if (status !== BackgroundFetch.STATUS_AVAILABLE) {
          // Uh-oh:  we have a problem:
          if (status === BackgroundFetch.STATUS_DENIED) {
            alert(
              'The user explicitly disabled background behavior for this app or for the whole system.'
            )
          } else if (status === BackgroundFetch.STATUS_RESTRICTED) {
            alert('Background updates are unavailable and the user cannot enable them again.')
          }
        }
      })
    }
  }
  void addCapBG()
}

export const stopAutoSync = () => {
  autoSync.stop()
}
